import { AvailableGroup } from '../components/editors/GroupEditor';

/**
 * Validates a subformula for selecting teams from groups based on specific conditions.
 *
 * Expected formats:
 * 1. "163,164,165-4,1"
 *    - Meaning: From groups 163, 164, and 165, compare teams in the 4th position and take the team in the 1st place among them.
 * 2. "148,149-3W"
 *    - Meaning: From groups 148 and 149, compare teams in the 3rd position and select the winner (W).
 *
 * Subformulas consist of two parts:
 * 1. **Group IDs:**
 *    - A list of one or more group IDs, represented as integers separated by commas (e.g., "163,164,165").
 * 2. **Team IDs:**
 *    - A list of one or more team positions, represented as integers.
 *    - Each team position may optionally end with "W" (winner) or "L" (loser), separated by commas (e.g., "4,1" or "3W,4L").
 *
 * Examples of valid and invalid subformulas:
 *
 * | Subformula           | Valid? | Reason                                                                 |
 * |----------------------|--------|------------------------------------------------------------------------|
 * | `"163-4,1"`          | ✅     | Valid; group 163, compare teams in 4th position, take the 1st.         |
 * | `"163,164-3W"`       | ✅     | Valid; groups 163 and 164, compare 3rd position teams, select winner.  |
 * | `"1,2,3-5,2"`        | ✅     | Valid; groups 1, 2, and 3, compare teams in 5th position, take the 2nd.|
 * | `"1,2,3-5,6"`        | ❌     | Invalid; "6" exceeds the number of groups (3).                        |
 * | `"10,20-3"`          | ❌     | Invalid; missing position to take from compared teams.                |
 * | `"10,20-3,4"`        | ❌     | Invalid; "4" exceeds the group count (2).                             |
 *
 * @param subformula - A string representing the subformula to validate.
 * @returns {boolean} - Returns true if the subformula is valid; otherwise, false.
 */
export const isValidSubformula = (subformula: string, availableGroups: AvailableGroup[]): boolean => {
  // Ignore all spaces in formulas
  subformula = subformula.replaceAll(' ', '');

  // Group ID(s) - one or more digits, separated by commas
  const groupIdPattern = /^(\d+)(,\d+)*$/;

  // Team ID(s) - one or more digits, optionally followed by W or L, separated by commas
  const teamIdPattern = /^(\d+([WL]?))(,\d+([WL]?))*$/;

  // Split the subformula into group ID(s) and team ID
  const parts = subformula.split('-');
  if (parts.length !== 2) return false;

  const groupPart = parts[0];
  const groupIds = groupPart.split(',');
  const lastPart = parts[1];

  // Validate practical constraint only when last character is not "W" or "L"
  if (!isNaN(Number(lastPart.slice(-1)))) {
    // last character is a digit
    const teamSelection = lastPart.split(',');
    let position = 0;
    if (teamSelection.length > 1) {
      const expectedNoOfGroups = Number(teamSelection[teamSelection.length - 1]);
      if (expectedNoOfGroups > groupIds.length) {
        return false; // Invalid: requested team exceeds the number of groups
      }
      position = Number(teamSelection[teamSelection.length - 2]);
    } else {
      position = Number(teamSelection[teamSelection.length - 1]);
    }
    if (position > 0) {
      let invalid = false;
      groupIds.forEach((groupId) => {
        let group = availableGroups.find((g) => g.id === Number(groupId));
        if (group && group.noOfTeams !== undefined && position > group.noOfTeams) {
          invalid = true;
        }
      });
      if (invalid) {
        return false; //Position is greater than number of configured teams for defined groups
      }
    }
  }

  // Validate the group part and the team part separately
  return groupIdPattern.test(groupPart) && teamIdPattern.test(lastPart);
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
